import React, { useState } from 'react';
import { Plus, Search, LayoutGrid, Edit2, Copy, Trash2, MoreVertical, Star, Eye, EyeOff } from 'lucide-react';
import {
  useTabConfigurations,
  useDeleteTabConfiguration,
  useCloneTabConfiguration,
  countVisibleTabs,
  getDefaultTab,
  type TabConfiguration
} from '../../../services/queries/tabConfigQueries';
import { DevModeBanner } from '../../common/DevModeBanner';
import { TabConfigBuilder } from './TabConfigBuilder';
import { useToast } from '../../../contexts/ToastContext';
import {
  StandardButton,
  StandardSearchBar,
  StandardSelect,
  StandardLoadingState,
  StandardErrorState,
  StandardEmptyState
} from '../../standard';

interface TabConfigHomeProps {
  objectName: string;
  objectLabel: string;
}

export const TabConfigHome: React.FC<TabConfigHomeProps> = ({
  objectName,
  objectLabel
}) => {
  const { success, error: showError } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [applicationFilter, setApplicationFilter] = useState<string>('all');
  const [builderOpen, setBuilderOpen] = useState<{ config?: TabConfiguration; objectName: string } | null>(null);
  const [menuOpen, setMenuOpen] = useState<string | null>(null);

  const { data: configs, isLoading, error } = useTabConfigurations(objectName);
  const deleteConfigMutation = useDeleteTabConfiguration();
  const cloneConfigMutation = useCloneTabConfiguration();

  const filteredConfigs = configs?.filter(config => {
    const matchesApp = applicationFilter === 'all' ||
                      (applicationFilter === 'none' && !config.application) ||
                      config.application === applicationFilter;

    return matchesApp;
  });

  // Get unique applications
  const applications = Array.from(new Set(configs?.map(c => c.application).filter(Boolean))) as string[];

  const totalConfigs = configs?.length || 0;
  const appSpecificConfigs = configs?.filter(c => c.application).length || 0;
  const totalTabs = configs?.reduce((sum, c) => sum + c.tabs.length, 0) || 0;

  const handleDelete = async (configId: string, appName: string) => {
    if (!window.confirm(`Are you sure you want to delete the tab configuration for "${appName}"?`)) return;

    try {
      await deleteConfigMutation.mutateAsync({ configId, objectName });
      success('Tab configuration deleted successfully!', `Configuration for ${appName} has been permanently removed`);
      setMenuOpen(null);
    } catch (error: any) {
      showError('Failed to delete tab configuration', error.message || 'Please try again');
    }
  };

  const handleClone = async (configId: string) => {
    try {
      const cloned = await cloneConfigMutation.mutateAsync(configId);
      setBuilderOpen({ config: cloned, objectName });
      setMenuOpen(null);
    } catch (error) {
      console.error('Failed to clone tab configuration:', error);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Tab Configuration</h2>
            <p className="text-gray-600 dark:text-gray-300 mt-1">
              Configure which tabs appear on {objectLabel} detail pages
            </p>
          </div>
          <StandardButton
            variant="primary"
            leftIcon={<Plus className="w-4 h-4" />}
            onClick={() => setBuilderOpen({ objectName })}
          >
            New Configuration
          </StandardButton>
        </div>

        {/* Development Mode Banner */}
        <DevModeBanner
          dataSource="mockDataManager.ts"
          itemCount={configs ? `${configs.length} tab configurations` : 'tab configurations'}
          customMessage={`Development Mode: ${configs ? `Showing ${configs.length} tab configurations` : 'Using mock data'} persisted via mockDataManager.ts (localStorage). Changes survive page refresh.`}
          dismissible={true}
        />

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {totalConfigs}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Total Configurations</div>
              </div>
              <div className="p-3 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                <LayoutGrid className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {appSpecificConfigs}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">App-Specific</div>
              </div>
              <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                <Star className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {totalTabs}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Total Tabs</div>
              </div>
              <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-lg">
                <LayoutGrid className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1">
            <StandardSearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search configurations..."
            />
          </div>

          <StandardSelect
            value={applicationFilter}
            onChange={(e) => setApplicationFilter(e.target.value)}
            options={[
              { value: 'all', label: 'All Applications' },
              { value: 'none', label: 'All Applications (Default)' },
              ...applications.map(app => ({ value: app, label: app }))
            ]}
          />
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <StandardLoadingState message="Loading configurations..." />
      )}

      {/* Error State */}
      {error && (
        <StandardErrorState
          title="Error loading tab configurations"
          message="Please try again."
        />
      )}

      {/* Configurations List */}
      {!isLoading && !error && (
        <>
          {filteredConfigs && filteredConfigs.length > 0 ? (
            <div className="space-y-3">
              {filteredConfigs.map((config) => (
                <ConfigCard
                  key={config.id}
                  config={config}
                  onEdit={() => setBuilderOpen({ config, objectName })}
                  onClone={() => handleClone(config.id)}
                  onDelete={() => handleDelete(config.id, config.application || 'All Applications')}
                  menuOpen={menuOpen === config.id}
                  onMenuToggle={() => setMenuOpen(menuOpen === config.id ? null : config.id)}
                />
              ))}
            </div>
          ) : (
            <StandardEmptyState
              icon={<LayoutGrid className="w-16 h-16" />}
              title={applicationFilter !== 'all' ? 'No configurations found' : 'No tab configurations yet'}
              message={applicationFilter !== 'all'
                ? 'Try adjusting your filter criteria'
                : 'Create your first tab configuration to get started'
              }
              actionLabel={applicationFilter !== 'all' ? 'Clear filters' : 'Create Configuration'}
              onAction={applicationFilter !== 'all'
                ? () => setApplicationFilter('all')
                : () => setBuilderOpen({ objectName })
              }
            />
          )}
        </>
      )}

      {/* Tab Configuration Builder Modal */}
      {builderOpen && (
        <TabConfigBuilder
          config={builderOpen.config}
          objectName={builderOpen.objectName}
          objectLabel={objectLabel}
          onClose={() => setBuilderOpen(null)}
        />
      )}
    </div>
  );
};

// Config Card Component
interface ConfigCardProps {
  config: TabConfiguration;
  onEdit: () => void;
  onClone: () => void;
  onDelete: () => void;
  menuOpen: boolean;
  onMenuToggle: () => void;
}

const ConfigCard: React.FC<ConfigCardProps> = ({
  config,
  onEdit,
  onClone,
  onDelete,
  menuOpen,
  onMenuToggle
}) => {
  const visibleTabs = countVisibleTabs(config);
  const defaultTab = getDefaultTab(config);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div className="p-3 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex-shrink-0">
          <LayoutGrid className="w-6 h-6 text-purple-600 dark:text-purple-400" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  {config.application || 'All Applications'}
                </h3>
                {!config.application && (
                  <span className="px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 text-xs rounded font-medium">
                    Default
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Tab configuration for {config.application || 'default'} view
              </p>
            </div>

            <div className="relative ml-4">
              <button
                onClick={onMenuToggle}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              >
                <MoreVertical className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              </button>

              {menuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={onMenuToggle}
                  />
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-20">
                    <div className="py-1">
                      <button
                        onClick={onEdit}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                      >
                        <Edit2 className="w-4 h-4" />
                        Edit Configuration
                      </button>
                      <button
                        onClick={onClone}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                      >
                        <Copy className="w-4 h-4" />
                        Clone Configuration
                      </button>
                      {config.application && (
                        <>
                          <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                          <button
                            onClick={onDelete}
                            className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete Configuration
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Tab Summary */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {config.tabs.slice(0, 5).map((tab, index) => (
              <div
                key={tab.id}
                className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 ${
                  tab.isVisible
                    ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                }`}
              >
                {tab.isVisible ? (
                  <Eye className="w-3 h-3" />
                ) : (
                  <EyeOff className="w-3 h-3" />
                )}
                {tab.label}
                {tab.isDefault && <Star className="w-3 h-3 fill-current" />}
              </div>
            ))}
            {config.tabs.length > 5 && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                +{config.tabs.length - 5} more
              </span>
            )}
          </div>

          {/* Metadata */}
          <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
            <div>{config.tabs.length} total tabs</div>
            <span>•</span>
            <div>{visibleTabs} visible</div>
            <span>•</span>
            <div>Default: {defaultTab?.label || 'None'}</div>
          </div>

          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Modified {new Date(config.lastModified).toLocaleDateString()} by {config.lastModifiedBy}
          </div>
        </div>
      </div>
    </div>
  );
};

